import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@stores/auth/authStore";
import { authApi } from "@api/modules/auth.api";
import { GlobalLoader } from "@components/loader";
import { TENANT_AUTH_LS_KEY } from "@lib/tenantPersistKeys";
import { isTenantRole, normalizeUser } from "@lib/normalizeUser";
import { isBillingOnlyPath } from "@lib/billingOnly";
import type { BroadcastConfig } from "@lib/broadcast";

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const billingOnly = useAuthStore((s) => s.billingOnly);
    const [isValid, setIsValid] = useState<boolean | null>(null);
    const [isHydrated, setIsHydrated] = useState(false);

    useEffect(() => {
        const run = async () => {
            const stored = localStorage.getItem(TENANT_AUTH_LS_KEY);

            if (!stored) {
                setIsHydrated(true);
                setIsValid(false);
                return;
            }

            try {
                const parsed = JSON.parse(stored);
                const storedUser = parsed?.state?.user;
                const storedToken = parsed?.state?.token;
                const storedAuth = parsed?.state?.isAuthenticated;

                if (!storedUser || !storedToken || !storedAuth || !isTenantRole(storedUser.role)) {
                    setIsHydrated(true);
                    setIsValid(false);
                    return;
                }

                const current = useAuthStore.getState();
                if (!current.user || !current.token) {
                    useAuthStore.setState({
                        user: normalizeUser(storedUser),
                        token: storedToken,
                        isAuthenticated: true,
                        billingOnly: parsed?.state?.billingOnly === true,
                        broadcast: (parsed?.state?.broadcast as BroadcastConfig | null) ?? null,
                    });
                }

                setIsHydrated(true);
                setIsValid(true);

                // Refresh the profile, plan and billing lock from the server in the background.
                authApi
                    .me()
                    .then((res) => {
                        const token = useAuthStore.getState().token;
                        if (res?.user && token) {
                            useAuthStore.getState().setAuth(normalizeUser(res.user), token, {
                                billingOnly: res.billingOnly === true,
                                broadcast: res.broadcast ?? useAuthStore.getState().broadcast,
                            });
                        }
                    })
                    .catch(() => {});
            } catch (e) {
                console.error("[ProtectedRoute] Parse error:", e);
                setIsHydrated(true);
                setIsValid(false);
            }
        };

        void run();

        const unsubscribe = useAuthStore.subscribe((state) => {
            if (state.user && state.token && state.isAuthenticated) {
                setIsValid(isTenantRole(state.user.role));
            } else {
                setIsValid(false);
            }
        });

        return () => unsubscribe();
    }, []);

    if (!isHydrated || isValid === null) {
        return <GlobalLoader />;
    }

    if (!isValid) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (billingOnly && !isBillingOnlyPath(location.pathname)) {
        return <Navigate to="/subscription" replace />;
    }

    return <>{children}</>;
};
